import { ShiftSwapStatus } from '../../../../models/shiftSwap';

// Single source of truth for shift-swap status presentation. Consumed by StatusBadge
// (the pill) and the hub's filter chips, so the label + color can never drift between
// the two surfaces. `label` is an English i18n key — wrap it in t() at the call site.
export interface SwapStatusMeta {
  /** English i18n key. */
  label: string;
  /** Tailwind classes for the small leading dot. */
  dot: string;
  /** Tailwind classes for the full-bordered pill (no side-stripe accents). */
  pill: string;
}

export const SWAP_STATUS_META: Record<ShiftSwapStatus, SwapStatusMeta> = {
  Pending: {
    label: 'Pending',
    dot: 'bg-amber-500',
    pill: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  },
  PeerAccepted: {
    label: 'Awaiting approval',
    dot: 'bg-blue-500',
    pill: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
  },
  Approved: {
    label: 'Approved',
    dot: 'bg-emerald-500',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  },
  Rejected: {
    label: 'Rejected',
    dot: 'bg-red-500',
    pill: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
  },
  // Declined (a peer said no) uses a warm orange to stay clearly distinct from the red
  // "Rejected" (a manager decision) it would otherwise collide with.
  Declined: {
    label: 'Declined',
    dot: 'bg-orange-500',
    pill: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800',
  },
  Cancelled: {
    label: 'Cancelled',
    dot: 'bg-slate-400',
    pill: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700',
  },
  // Expired is a hollow (transparent) pill so it reads differently from the filled
  // "Cancelled" even though both sit in the neutral slate family: color + fill disambiguate.
  Expired: {
    label: 'Expired',
    dot: 'bg-slate-400',
    pill: 'bg-transparent text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600',
  },
};

/** English i18n key for a status. Wrap in t() at the call site. */
export function swapStatusLabel(status: ShiftSwapStatus): string {
  return (SWAP_STATUS_META[status] ?? SWAP_STATUS_META.Pending).label;
}
